import { CustomSurveyTemplate, TemplateScope } from '../../api/customSurveyTemplates';

export type StatusFilter = 'all' | 'active' | 'archived';
export type ScopeFilter = 'all' | 'company' | 'team';
export type SortOption = 'lastUsed' | 'mostUsed' | 'name';

export type TemplateFilters = {
  query: string;
  status: StatusFilter;
  scope: ScopeFilter;
  sort: SortOption;
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Kategorier lagres som kommaseparert tekst, for eksempel «Trivsel, Organisasjon» */
export const categoriesOf = (template: CustomSurveyTemplate) =>
  (template.category ?? '')
    .split(',')
    .map((category) => category.trim())
    .filter(Boolean);

/** Hele dager siden malen sist ble brukt, eller null når den aldri er brukt */
export const daysSinceUsed = (lastUsed: string | null | undefined, today = new Date()) => {
  if (!lastUsed) return null;
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const used = new Date(lastUsed);
  const usedDay = new Date(used.getFullYear(), used.getMonth(), used.getDate()).getTime();
  return Math.max(0, Math.round((start - usedDay) / DAY_MS));
};

export const templateStats = (templates: CustomSurveyTemplate[], today = new Date()) => {
  const archived = templates.filter((template) => template.isArchived).length;
  return {
    total: templates.length,
    active: templates.length - archived,
    archived,
    recentlyUsed: templates.filter((template) => {
      const days = daysSinceUsed(template.lastUsed, today);
      return days !== null && days <= 30;
    }).length,
  };
};

const matchesStatus = (template: CustomSurveyTemplate, status: StatusFilter) =>
  status === 'all' || (status === 'archived') === template.isArchived;

const matchesScope = (template: CustomSurveyTemplate, scope: ScopeFilter) =>
  scope === 'all' || (scope === 'team') === (template.scope === TemplateScope.TEAM);

const matchesQuery = (template: CustomSurveyTemplate, query: string) => {
  const search = query.trim().toLowerCase();
  if (!search) return true;
  return [template.name, template.description, template.category].some((text) =>
    text?.toLowerCase().includes(search),
  );
};

const usedTime = (template: CustomSurveyTemplate) =>
  template.lastUsed ? new Date(template.lastUsed).getTime() : -Infinity;

const byName = (a: CustomSurveyTemplate, b: CustomSurveyTemplate) => a.name.localeCompare(b.name, 'nb');

const sorters: Record<SortOption, (a: CustomSurveyTemplate, b: CustomSurveyTemplate) => number> = {
  lastUsed: (a, b) => usedTime(b) - usedTime(a) || byName(a, b),
  mostUsed: (a, b) => (b.surveysCreated ?? 0) - (a.surveysCreated ?? 0) || byName(a, b),
  name: byName,
};

/** Malene som passer søket, statusen og hvem de gjelder for, i valgt rekkefølge */
export const visibleTemplates = (templates: CustomSurveyTemplate[], filters: TemplateFilters) =>
  templates
    .filter(
      (template) =>
        matchesStatus(template, filters.status) &&
        matchesScope(template, filters.scope) &&
        matchesQuery(template, filters.query),
    )
    .sort(sorters[filters.sort]);
